import { bookingRefundOffers } from "../../../app/api/cancelBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import {
  getRecoilStateValue,
  updateRecoilState,
} from "../../../Orchestrator/Recoils/Events/Recoil";
import type { IRefundQuoteEntry } from "../../../Orchestrator/Recoils/AddonsAtoms";
import { AncillaryStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../../Orchestrator/Types/Index";
import { ticketFulfillmentIdsForNolLegs } from "../../../Components/OnwardTravel/nolTicketRefund";

type IResult = { status: "success" | "fail"; message?: string };

/**
 * Speculatively POSTs /bookings/{id}/refund-offers and caches the per-id
 * refundFee + refundableAmount in refundOffersByFulfillmentIdAtom.
 *
 * Two callers, two reasons:
 *  - manage-seating entry (ManageSeatingRedirectClickAction), for every
 *    FULFILLED ancillary id — meals and addons;
 *  - booking-details load (GetBookingDetailDataAction), gated on the booking
 *    holding a paid Nol pass, for that leg's TRAIN TICKET.
 *
 * The two id sets go out as SEPARATE requests; see the ticketIds note in run().
 *
 * Display-only under the new server-side refund pipeline: AddonsModal
 * reads the cache to show accurate per-meal refund values before payment,
 * instead of approximating with `removedBaselineTotal − bookingWideModFee`.
 * Actual refund settlement no longer flows through this endpoint — it
 * happens at gateway-session settlement via the `refundedAncillaries`
 * field of POST /api/payment/session. The cache exists purely for the UI.
 *
 * Non-fatal: a missing/stale cache just makes the modal fall back to the
 * naive math; the pay-time refund still settles correctly because the
 * backend doesn't depend on these cached refundOfferIds.
 *
 * Mapping note: the pay-time path stores ancillary `id`s in the
 * `fulfillmentIds` field of pendingRefundFulfillmentIdsAtom and forwards
 * them as-is to the session POST, which works because the backend accepts
 * ancillary ids as fulfillment ids on these endpoints. We follow the same
 * convention here. The cache is keyed by fulfillment id, which today means
 * ancillary ids from the addons sweep and ADMISSION ids from the ticket sweep —
 * see nolTicketRefund.ts, which reasons about that namespace.
 */
export class ProbeRefundOffersAction extends BaseAction<IResult, void> {
  protected override deduplicate = true;

  override async run(): Promise<IResult> {
    const bookingResponse = getRecoilStateValue<IBookingResponse>(
      "bookingResponseAtom",
    );
    const bookingId = bookingResponse?.booking?.id || "";
    if (!bookingId) {
      return { status: "fail", message: "Missing bookingId" };
    }

    const fulfillmentIds: string[] = [];
    for (const offer of bookingResponse?.booking?.bookedOffers || []) {
      for (const anc of (offer as any).ancillaries || []) {
        if (
          (anc as any)?.status === AncillaryStatusEnum.FULFILLED &&
          (anc as any)?.id
        ) {
          fulfillmentIds.push((anc as any).id);
        }
      }
    }

    /**
     * The TRAIN TICKET of any leg holding a Nol pass, quoted SEPARATELY.
     *
     * A pass follows its ticket, so NolCardModal needs the ticket's number to
     * decide whether to offer a refund at all (nolRefundBlockedByTicket).
     *
     * A second POST rather than more ids in the first, because the fallback
     * below divides an offer's AGGREGATE evenly across the fulfillments a
     * missing `refundOfferBreakDown` did not reach. Bundle a AED 24 fare with
     * two meals and that fallback hands each meal a share of the fare — a
     * number AddonsModal renders to the passenger on a surface that has nothing
     * to do with this feature. Separating the requests keeps the ancillary body
     * byte-identical to what it has always been, so no existing surface can
     * move.
     */
    const ticketIds = ticketFulfillmentIdsForNolLegs(bookingResponse ?? null);

    if (fulfillmentIds.length === 0 && ticketIds.length === 0) {
      // Nothing refundable to price — clear any stale cache and bail. Don't
      // POST: OSDM rejects empty fulfillmentIds with 400.
      updateRecoilState("refundOffersByFulfillmentIdAtom", null);
      return { status: "success" };
    }

    try {
      const entries: Record<string, IRefundQuoteEntry> = {};
      let anyOk = false;

      if (fulfillmentIds.length > 0) {
        anyOk = (await this.quoteInto(entries, bookingId, fulfillmentIds)) || anyOk;
      }
      // Its own request — see the ticketIds note above. A failure here must not
      // discard the ancillary quotes: the Refund control simply stays visible.
      if (ticketIds.length > 0) {
        anyOk = (await this.quoteInto(entries, bookingId, ticketIds)) || anyOk;
      }

      if (!anyOk) return { status: "fail", message: "POST failed" };

      updateRecoilState("refundOffersByFulfillmentIdAtom", {
        bookingId,
        entries,
      });
      return { status: "success" };
    } catch (error: any) {
      console.warn(
        "[ProbeRefundOffersAction] probe failed (non-fatal):",
        error?.message,
      );
      return { status: "fail", message: error?.message };
    }
  }

  /**
   * POST one batch and stamp its per-fulfillment values into `entries`.
   * Returns false on a non-200 so the caller can tell "priced nothing" from
   * "priced zero" — the distinction the refund gates depend on.
   *
   * Public for nolPassesBlockedByTicket, which prices tickets at cancel time
   * with the same per-id parsing.
   */
  async quoteInto(
    entries: Record<string, IRefundQuoteEntry>,
    bookingId: string,
    fulfillmentIds: string[],
  ): Promise<boolean> {
    const response: any = await bookingRefundOffers(
      { fulfillmentIds, overruleCode: null, refundDate: new Date().toISOString() },
      bookingId,
    );
    const ok = response?.code === 200 || response?.status === "ok";
    if (!ok) {
      console.warn("[ProbeRefundOffersAction] POST /refund-offers failed:", {
        status: response?.status,
        code: response?.code,
        errorCode: response?.errorCode ?? response?.answer?.errorCode,
        traceId: response?.answer?.traceId,
        requested: fulfillmentIds.length,
      });
      return false;
    }

    /**
     * Stamp ONLY what this batch asked about.
     *
     * Separating the requests keeps each BODY correct, but the RESPONSE is
     * whatever the retailer names back, and a refund offer for an admission may
     * legitimately cover its ancillaryRefs (docs/retailer.json,
     * Admission.ancillaryRefs). The ticket batch runs second, so an ancillary id
     * appearing in its offer would overwrite that meal's own quote with a share
     * of the FARE — the number AddonsModal renders to the passenger on
     * manage-seating. Cheap to prevent, expensive to notice.
     */
    const requested = new Set(fulfillmentIds);
    const refundOffers: any[] = response?.answer?.refundOffers || [];
    for (const offer of refundOffers) {
      const offerId: string = offer?.id || "";
      if (!offerId) continue;
      const aggregateFee = calculatePrice(offer?.refundFee) || 0;
      const aggregateAmount = calculatePrice(offer?.refundableAmount) || 0;
      const fulfillments: any[] = offer?.fulfillments || [];

      // OSDM groups multiple parts under one refund offer when the request
      // bundles them. Per-part values live in `refundOfferBreakDown[]` — each
      // item has its own refundFee + refundableAmount and a `bookingParts[]`
      // array referencing the fulfillment(s) it covers. Fall back to an even
      // split of the aggregate when the breakdown is missing/sparse, and to the
      // aggregate-as-each (legacy behaviour) when even fulfillments are missing.
      const breakdown: any[] = offer?.refundOfferBreakDown || [];
      /**
       * Two different sets, deliberately.
       *
       * `breakdownCovered` records EVERY fulfillment the breakdown priced, even
       * ones this batch did not ask about — it is the DENOMINATOR's business,
       * and narrowing it would divide the aggregate fewer ways and inflate each
       * share. AddonsModal renders those shares as a per-meal refund estimate,
       * and inflating a refund estimate is the wrong direction to be wrong in.
       *
       * `requested` gates only what we WRITE. See the note above: a ticket
       * offer may name an ancillary, and the ticket batch runs second.
       */
      const breakdownCovered = new Set<string>();
      for (const item of breakdown) {
        const itemFee = calculatePrice(item?.refundFee) || 0;
        const itemAmount = calculatePrice(item?.refundableAmount) || 0;
        for (const part of (item?.bookingParts as any[]) || []) {
          const fid: string = part?.id || "";
          if (!fid) continue;
          breakdownCovered.add(fid);
          if (!requested.has(fid)) continue;
          entries[fid] = {
            refundOfferId: offerId,
            refundFee: itemFee,
            refundableAmount: itemAmount,
          };
        }
      }

      const remaining = fulfillments.filter((f) => {
        const fid: string = f?.id || "";
        return fid && !breakdownCovered.has(fid);
      });
      if (remaining.length > 0) {
        const splitFee = aggregateFee / remaining.length;
        const splitAmount = aggregateAmount / remaining.length;
        for (const f of remaining) {
          const fid: string = f?.id || "";
          if (!fid || !requested.has(fid)) continue;
          entries[fid] = {
            refundOfferId: offerId,
            refundFee: splitFee,
            refundableAmount: splitAmount,
          };
        }
      }
    }
    return true;
  }
}

export const probeRefundOffersAction = new ProbeRefundOffersAction();
